const safeNumber = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const getSafeObject = (obj) => (obj && typeof obj === 'object' ? obj : {});

export const US_STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming', DC: 'District of Columbia', PR: 'Puerto Rico', VI: 'U.S. Virgin Islands', GU: 'Guam', MP: 'Northern Mariana Islands', AS: 'American Samoa', FM: 'Federated States of Micronesia', MH: 'Marshall Islands', PW: 'Palau'
};

const getStateFullName = (stateCode) => {
  if (!stateCode || typeof stateCode !== 'string') return 'Unknown';
  const normalized = stateCode.trim().toUpperCase();
  return US_STATE_NAMES[normalized] || stateCode;
};

export const getTopKPIs = (rawData = []) => {
  const totals = rawData.reduce(
    (acc, item) => {
      const totalCount = getSafeObject(item.total_count);
      return {
        patient_count: acc.patient_count + safeNumber(totalCount.patient_count),
        appointment_count: acc.appointment_count + safeNumber(totalCount.appointment_count),
        visit_count: acc.visit_count + safeNumber(totalCount.visit_count),
      };
    },
    { patient_count: 0, appointment_count: 0, visit_count: 0 }
  );

  return {
    patients: totals.patient_count,
    appointments: totals.appointment_count,
    visits: totals.visit_count,
    facilities: rawData.length,
  };
};

export const transformProviderPerformance = (rawData = []) => {
  const providers = Object.values(rawData.reduce((acc, item) => {
    const providerMap = getSafeObject(item.providers);
    Object.entries(providerMap).forEach(([provider, details]) => {
      const safeDetails = getSafeObject(details);
      const revenue = safeNumber(safeDetails.sum_insurance_pay) + safeNumber(safeDetails.sum_patient_payment) + safeNumber(safeDetails.sum_copay) + safeNumber(safeDetails.sum_adjustment);
      const count = safeNumber(safeDetails.visit_count);
      if (!acc[provider]) {
        acc[provider] = { provider, revenue: 0, appointments: 0 };
      }
      acc[provider].revenue += revenue;
      acc[provider].appointments += count;
    });
    return acc;
  }, {}));

  const sortedProviders = providers
    .map((provider) => ({
      name: provider.provider,
      revenue: provider.revenue,
      count: provider.appointments,
    }))
    .sort((a, b) => b.revenue - a.revenue);

  const totalRevenue = sortedProviders.reduce((sum, provider) => sum + provider.revenue, 0);
  let cumulativeRevenue = 0;

  return sortedProviders.map((provider) => {
    cumulativeRevenue += provider.revenue;
    const contributionPercent = totalRevenue > 0 ? (provider.revenue / totalRevenue) * 100 : 0;
    const cumulativePercent = totalRevenue > 0 ? (cumulativeRevenue / totalRevenue) * 100 : 0;

    return {
      ...provider,
      contributionPercent: Number(contributionPercent.toFixed(1)),
      cumulativePercent: Number(cumulativePercent.toFixed(1)),
    };
  });
};

export const transformFacilityPatients = (rawData = []) => {
  return rawData
    .map((item) => ({
      name: item.facility_name || item.facility_code || 'Unknown Facility',
      value: safeNumber(getSafeObject(item.total_count).patient_count),
    }))
    .sort((a, b) => b.value - a.value);
};

export const transformAppointmentStatus = (rawData = []) => {
  const aggregated = rawData.reduce((acc, item) => {
    const status = getSafeObject(item.patient_visit_status);
    Object.entries({
      seen: status.seen_count,
      made: status.made_count,
      cancelled: status.cancelled_count,
      deleted: status.deleted_count,
      no_show: status.no_show_count,
      moved: status.moved_count,
      arrived: status.arrived_count,
      other: status.other_count,
    }).forEach(([key, value]) => {
      acc[key] = safeNumber(acc[key]) + safeNumber(value);
    });
    return acc;
  }, {});

  return [
    { name: 'Seen', value: aggregated.seen },
    { name: 'Made', value: aggregated.made },
    { name: 'Cancelled', value: aggregated.cancelled },
    { name: 'Deleted', value: aggregated.deleted },
    { name: 'No Show', value: aggregated.no_show },
    { name: 'Moved', value: aggregated.moved },
    { name: 'Arrived', value: aggregated.arrived },
    { name: 'Other', value: aggregated.other },
  ].filter((item) => item.value > 0);
};

export const transformFacilityPerformanceRadar = (rawData = []) => {
  return rawData.map((item) => {
    const total = getSafeObject(item.total_count);
    const status = getSafeObject(item.patient_visit_status);
    return {
      name: item.facility_name || item.facility_code || 'Facility',
      values: [
        safeNumber(total.patient_count),
        safeNumber(total.appointment_count),
        safeNumber(total.visit_count),
        safeNumber(status.seen_count),
        safeNumber(status.cancelled_count),
      ],
    };
  });
};

const normalizeAgeRanges = (data) => {
  const ageKeys = ['4_9', '10_12', '13_17', '18_21', '22_30', '31_40', '41_50', '51_60', '61_70', '71_80', '80_plus'];
  const ranges = getSafeObject(data);
  return ageKeys.map((key) => ({
    name: key.replace('_', '-').replace('plus', '+'),
    value: safeNumber(ranges[key]),
  }));
};

export const transformAgeDistribution = (rawData = []) => {
  const aggregated = rawData.reduce((acc, item) => {
    normalizeAgeRanges(getSafeObject(item.appointment_age_ranges)).forEach(({ name, value }) => {
      acc[name] = safeNumber(acc[name]) + value;
    });
    return acc;
  }, {});

  return Object.entries(aggregated).map(([name, value]) => ({ name, value }));
};

export const transformSeenAgeDistribution = (rawData = []) => {
  const aggregated = rawData.reduce((acc, item) => {
    normalizeAgeRanges(getSafeObject(item.seen_age_ranges)).forEach(({ name, value }) => {
      acc[name] = safeNumber(acc[name]) + value;
    });
    return acc;
  }, {});

  return Object.entries(aggregated).map(([name, value]) => ({ name, value }));
};

export const transformStateDistribution = (rawData = []) => {
  const aggregated = rawData.reduce((acc, item) => {
    const stateCounts = getSafeObject(getSafeObject(item.patient_place).appointment_place).state;
    const itemRevenue = Object.values(getSafeObject(item.providers)).reduce((rev, provider) => {
      const providerData = getSafeObject(provider);
      const cptMap = getSafeObject(providerData.cpt_map);
      return Object.values(getSafeObject(cptMap)).reduce((sum, cptValues) => {
        return (
          sum +
          safeNumber(cptValues.sum_insurance_pay) +
          safeNumber(cptValues.sum_patient_payment) +
          safeNumber(cptValues.sum_copay) +
          safeNumber(cptValues.sum_adjustment)
        );
      }, rev);
    }, 0);

    const facilityTotalStateCount = Object.values(getSafeObject(stateCounts)).reduce((sum, count) => sum + safeNumber(count), 0);
    const totalCount = facilityTotalStateCount || 1;
    const facilityTotals = getSafeObject(item.total_count);
    const facilityPatientCount = safeNumber(facilityTotals.patient_count);
    const facilityVisitCount = safeNumber(facilityTotals.visit_count);

    Object.entries(getSafeObject(stateCounts)).forEach(([state, count]) => {
      const normalizedState = state.toString().trim().toUpperCase();
      const stateCount = safeNumber(count);
      const ratio = facilityTotalStateCount ? stateCount / totalCount : 0;
      if (!acc[normalizedState]) {
        acc[normalizedState] = {
          code: normalizedState,
          name: getStateFullName(normalizedState),
          value: 0,
          patients: 0,
          appointments: 0,
          visits: 0,
          revenue: 0,
        };
      }

      acc[normalizedState].value += stateCount;
      acc[normalizedState].appointments += stateCount;
      acc[normalizedState].patients += Math.round(facilityPatientCount * ratio);
      acc[normalizedState].visits += Math.round(facilityVisitCount * ratio);
      acc[normalizedState].revenue += itemRevenue * ratio;
    });

    return acc;
  }, {});

  return Object.values(aggregated)
    .map((item) => ({
      ...item,
      revenue: Number(item.revenue.toFixed(2)),
    }))
    .sort((a, b) => b.value - a.value);
};

export const transformCityDistribution = (rawData = []) => {
  const aggregated = rawData.reduce((acc, item) => {
    const cityCounts = getSafeObject(getSafeObject(item.patient_place).appointment_place).city;
    Object.entries(getSafeObject(cityCounts)).forEach(([city, count]) => {
      const normalized = city.toString().trim();
      acc[normalized] = safeNumber(acc[normalized]) + safeNumber(count);
    });
    return acc;
  }, {});

  return Object.entries(aggregated)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);
};

export const transformPatientsAppointmentsVisits = (rawData = []) => {
  return rawData.map((item) => {
    const total = getSafeObject(item.total_count);
    return {
      name: item.facility_name || item.facility_code || 'Facility',
      patients: safeNumber(total.patient_count),
      appointments: safeNumber(total.appointment_count),
      visits: safeNumber(total.visit_count),
    };
  });
};

export const transformFacilityTreemap = (rawData = []) => {
  return rawData.map((item) => ({
    name: item.facility_name || item.facility_code || 'Facility',
    value: safeNumber(getSafeObject(item.total_count).appointment_count),
  }));
};

export const transformAgeHeatmap = (rawData = []) => {
  const ageKeys = ['4_9', '10_12', '13_17', '18_21', '22_30', '31_40', '41_50', '51_60', '61_70', '71_80', '80_plus'];
  const rows = rawData.map((item) => {
    const ranges = getSafeObject(item.appointment_age_ranges);
    return ageKeys.map((key) => safeNumber(ranges[key]));
  });
  const facilityNames = rawData.map((item) => item.facility_name || item.facility_code || 'Facility');
  return { facilityNames, ageKeys, rows };
};

export const transformFinancialScatter = (rawData = []) => {
  const points = [];
  rawData.forEach((item) => {
    const facilityName = item.facility_name || item.facility_code || 'Facility';
    const providerMap = getSafeObject(item.providers);
    Object.entries(providerMap).forEach(([provider, details]) => {
      const safeDetails = getSafeObject(details);
      const cptMap = getSafeObject(safeDetails.cpt_map);
      Object.entries(cptMap).forEach(([cpt, cptValues]) => {
        const insurancePayRaw = safeNumber(cptValues.sum_insurance_pay);
        const patientPaymentRaw = safeNumber(cptValues.sum_patient_payment || 0) + safeNumber(cptValues.sum_copay || 0);
        // floor at 1 so log-scale axes don't drop zero-value points
        const insurancePay = Math.max(insurancePayRaw, 1);
        const patientPayment = Math.max(patientPaymentRaw, 1);
        const visitCount = safeNumber(cptValues.visit_count) || safeNumber(cptValues.count) || safeNumber(safeDetails.visit_count) || 1;
        points.push({
          value: [insurancePay, patientPayment, visitCount],
          provider,
          facilityName,
          cpt,
          visitCount,
          rawInsurancePay: insurancePayRaw,
          rawPatientPayment: patientPaymentRaw,
        });
      });
    });
  });
  return points;
};

export const transformCompletionGauge = (rawData = []) => {
  const totals = rawData.reduce(
    (acc, item) => {
      const status = getSafeObject(item.patient_visit_status);
      acc.seen += safeNumber(status.seen_count);
      acc.appointments += safeNumber(item.total_count?.appointment_count);
      return acc;
    },
    { seen: 0, appointments: 0 }
  );
  const value = totals.appointments > 0 ? Math.round((totals.seen / totals.appointments) * 100) : 0;
  return { value, seen: totals.seen, appointments: totals.appointments };
};

export const transformAppointmentFunnel = (rawData = []) => {
  const totals = rawData.reduce(
    (acc, item) => {
      const status = getSafeObject(item.patient_visit_status);
      acc.appointments += safeNumber(item.total_count?.appointment_count);
      acc.made += safeNumber(status.made_count);
      acc.seen += safeNumber(status.seen_count);
      return acc;
    },
    { appointments: 0, made: 0, seen: 0 }
  );
  return [
    { name: 'Appointments', value: totals.appointments },
    { name: 'Made', value: totals.made },
    { name: 'Seen', value: totals.seen },
    { name: 'Completed', value: totals.seen },
  ];
};

export const transformSunburst = (rawData = []) => {
  const orgMap = {};
  rawData.forEach((item) => {
    const orgName = item.organization_name?.trim() || 'Organization';
    const facilityName = item.facility_name?.trim() || item.facility_code || 'Facility';
    const status = item.patient_visit_status?.seen_count > 0 ? 'Seen' : 'Made';
    orgMap[orgName] = orgMap[orgName] || {};
    orgMap[orgName][facilityName] = orgMap[orgName][facilityName] || {};
    orgMap[orgName][facilityName][status] = safeNumber(orgMap[orgName][facilityName][status]) + 1;
  });
  return Object.entries(orgMap).map(([orgName, facilities]) => ({
    name: orgName,
    children: Object.entries(facilities).map(([facilityName, statuses]) => ({
      name: facilityName,
      children: Object.entries(statuses).map(([status, value]) => ({ name: status, value })),
    })),
  }));
};

export const transformFinancialByFacility = (rawData = []) => {
  const rows = rawData.map((item) => {
    const facilityName = item.facility_name || item.facility_code || 'Facility';
    const providerMap = getSafeObject(item.providers);
    let insurance = 0;
    let patient = 0;
    let visits = 0;

    Object.values(providerMap).forEach((details) => {
      const safeDetails = getSafeObject(details);
      insurance += safeNumber(safeDetails.sum_insurance_pay);
      patient += safeNumber(safeDetails.sum_patient_payment) + safeNumber(safeDetails.sum_copay);
      visits += safeNumber(safeDetails.visit_count);
    });

    return { name: facilityName, insurance, patient, visits, total: insurance + patient };
  });

  return rows.sort((a, b) => b.total - a.total);
};

export const transformSankey = (rawData = []) => {
  const nodes = [];
  const nodeMap = new Map();
  const links = [];

  const addNode = (name, type) => {
    let suffix = '';
    if (type === 'org') suffix = '\u200b';
    else if (type === 'facility') suffix = '\u200c';
    else if (type === 'provider') suffix = '\u200d';

    const uniqueName = name + suffix;

    if (!nodeMap.has(uniqueName)) {
      const index = nodes.length;
      nodes.push({ name: uniqueName });
      nodeMap.set(uniqueName, index);
    }
    return nodeMap.get(uniqueName);
  };

  rawData.forEach((item) => {
    const org = item.organization_name?.trim() || 'Organization';
    const facility = item.facility_name?.trim() || item.facility_code || 'Facility';
    const providerMap = getSafeObject(item.providers);
    Object.keys(providerMap).forEach((provider) => {
      const orgIndex = addNode(org, 'org');
      const facilityIndex = addNode(facility, 'facility');
      const providerIndex = addNode(provider, 'provider');
      links.push({ source: orgIndex, target: facilityIndex, value: 1 });
      links.push({ source: facilityIndex, target: providerIndex, value: 1 });
    });
  });

  return { nodes, links };
};

const VISIT_FREQ_LABELS = {
  '0_visits': '0 Visits', '1_visit': '1 Visit', '2_3_visits': '2-3 Visits',
  '4_6_visits': '4-6 Visits', '7_plus_visits': '7+ Visits',
};
const VISIT_FREQ_ORDER = ['0_visits', '1_visit', '2_3_visits', '4_6_visits', '7_plus_visits'];

const NO_SHOW_LABELS = {
  '0_no_shows': 'No No-Shows', '1_no_show': '1 No-Show', '2_plus_no_shows': '2+ No-Shows',
};
const NO_SHOW_ORDER = ['0_no_shows', '1_no_show', '2_plus_no_shows'];

const RECENCY_LABELS = {
  active_0_30d: 'Active (0-30d)', recent_31_90d: 'Recent (31-90d)', cooling_91_180d: 'Cooling (91-180d)',
  at_risk_181_365d: 'At Risk (181-365d)', lapsed_365d_plus: 'Lapsed (365d+)',
};
const RECENCY_ORDER = ['active_0_30d', 'recent_31_90d', 'cooling_91_180d', 'at_risk_181_365d', 'lapsed_365d_plus'];

const LTV_LABELS = {
  0: '$0', '1_100': '$1-100', '101_500': '$101-500', '501_1000': '$501-1000', '1000_plus': '$1000+',
};
const LTV_ORDER = ['0', '1_100', '101_500', '501_1000', '1000_plus'];

export const transformVisitFrequency = (data) => {
  const distRaw = data?.visit_frequency_distribution;
  if (!distRaw) return null;
  const dist = getSafeObject(distRaw);
  return VISIT_FREQ_ORDER.map((key) => ({ name: VISIT_FREQ_LABELS[key], value: safeNumber(dist[key]) }));
};

export const transformNoShowRisk = (data) => {
  const distRaw = data?.no_show_risk_distribution;
  if (!distRaw) return null;
  const dist = getSafeObject(distRaw);
  return NO_SHOW_ORDER.map((key) => ({ name: NO_SHOW_LABELS[key], value: safeNumber(dist[key]) }))
    .filter((item) => item.value > 0);
};

export const transformRecencyFunnel = (data) => {
  const distRaw = data?.recency_distribution;
  if (!distRaw) return null;
  const dist = getSafeObject(distRaw);
  return RECENCY_ORDER.map((key) => ({ name: RECENCY_LABELS[key], value: safeNumber(dist[key]) }));
};

export const transformLTVCount = (data) => {
  const distRaw = data?.lifetime_value_distribution;
  if (!distRaw) return null;
  const dist = getSafeObject(distRaw);
  return LTV_ORDER.map((key) => ({ name: LTV_LABELS[key], value: safeNumber(getSafeObject(dist[key]).patient_count) }));
};

export const transformLTVRevenue = (data) => {
  const distRaw = data?.lifetime_value_distribution;
  if (!distRaw) return null;
  const dist = getSafeObject(distRaw);
  return LTV_ORDER.map((key) => ({
    name: LTV_LABELS[key],
    value: Number(safeNumber(getSafeObject(dist[key]).total_revenue).toFixed(2)),
  })).filter((item) => item.value > 0);
};

export const transformAcquisitionTrend = (data) => {
  if (!Array.isArray(data?.acquisition_trend) || data.acquisition_trend.length === 0) return null;
  const trend = data.acquisition_trend.slice();
  return trend.sort((a, b) => (a.month > b.month ? 1 : -1))
    .map((item) => ({ name: item.month, value: safeNumber(item.new_patient_count) }));
};

export const transformRetentionGauge = (data) => {
  if (data == null || data?.retention_rate == null) return null;
  return { value: safeNumber(data.retention_rate) };
};

export const transformEngagementRadar = (data) => {
  const scoreRaw = data?.engagement_score;
  if (!scoreRaw) return null;
  const score = getSafeObject(scoreRaw);
  return [
    safeNumber(score.show_rate),
    safeNumber(score.retention_rate),
    safeNumber(score.visit_frequency_score),
    safeNumber(score.revenue_score),
    safeNumber(score.low_no_show_score),
  ];
};

export const transformDailyActiveCalendar = (data) => {
  const dailyRaw = data?.daily_active_patients;
  if (!Array.isArray(dailyRaw) || dailyRaw.length === 0) return null;
  const daily = dailyRaw;
  const points = daily.map((item) => [item.date, safeNumber(item.active_patients)]);
  const dates = daily.map((item) => item.date).sort();
  return {
    points,
    startDate: dates[0] || data?.date_range?.start_date,
    endDate: dates[dates.length - 1] || data?.date_range?.end_date,
  };
};

export const transformFrequencyRecencySankey = (data) => {
  const flowRaw = data?.frequency_to_recency_flow;
  if (!flowRaw) return null;
  const flow = getSafeObject(flowRaw);
  const nodeNames = Array.isArray(flow.nodes) ? flow.nodes : [];
  const rawLinks = Array.isArray(flow.links) ? flow.links : [];

  const nodes = nodeNames.map((name) => ({ name: VISIT_FREQ_LABELS[name] || RECENCY_LABELS[name] || name }));
  const links = rawLinks
    .map((link) => ({
      source: VISIT_FREQ_LABELS[link.source] || RECENCY_LABELS[link.source] || link.source,
      target: VISIT_FREQ_LABELS[link.target] || RECENCY_LABELS[link.target] || link.target,
      value: safeNumber(link.value),
    }))
    .filter((link) => link.value > 0);

  if (nodes.length === 0 && links.length === 0) return null;

  return { nodes, links };
};

export const transformVisitCountBoxplot = (data) => {
  const boxRaw = data?.visit_count_boxplot;
  if (!Array.isArray(boxRaw) || boxRaw.length === 0) return null;
  const box = boxRaw;
  return box.map((v) => safeNumber(v));
};

export const transformCPTPatientDistribution = (data) => {
  if (!data?.cpt_patient_distribution) return [];

  return data.cpt_patient_distribution
    .map((item) => ({
      name: item.cpt_code,
      value: item.patient_count,
    }))
    .sort((a, b) => b.value - a.value);
};

// ---------- Provider Analytics ----------

const toNameValueList = (items, nameKey, valueKey, limit) => {
  if (!Array.isArray(items)) return [];
  const list = items
    .map((item) => ({ name: item[nameKey] || 'Unknown', value: safeNumber(item[valueKey]) }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
  return limit ? list.slice(0, limit) : list;
};

const mapToNameValueList = (map, limit) => {
  const entries = Object.entries(getSafeObject(map));
  const list = entries
    .map(([name, value]) => ({ name, value: safeNumber(value) }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
  return limit ? list.slice(0, limit) : list;
};

export const transformProviderVisitStatus = (provider) => {
  const status = getSafeObject(provider?.visit_status);
  return [
    { name: 'Seen', value: safeNumber(status.seen_count) },
    { name: 'Made', value: safeNumber(status.made_count) },
    { name: 'Cancelled', value: safeNumber(status.cancelled_count) },
    { name: 'Deleted', value: safeNumber(status.deleted_count) },
    { name: 'No Show', value: safeNumber(status.no_show_count) },
    { name: 'Moved', value: safeNumber(status.moved_count) },
    { name: 'Arrived', value: safeNumber(status.arrived_count) },
    { name: 'Other', value: safeNumber(status.other_count) },
  ].filter((item) => item.value > 0);
};

export const transformProviderAgeRanges = (provider) => normalizeAgeRanges(provider?.patient_age_ranges);

export const transformProviderStateDistribution = (provider) => mapToNameValueList(provider?.patient_place?.state, 15);

export const transformProviderCityDistribution = (provider) => mapToNameValueList(provider?.patient_place?.city, 15);

export const transformProviderCPTMix = (provider) => toNameValueList(provider?.cpt_mix, 'cpt_code', 'visit_count');

export const transformProviderICD10Mix = (provider) => toNameValueList(provider?.icd10_mix, 'icd_code_10', 'visit_count');

export const transformProviderFacilities = (provider) => toNameValueList(provider?.facilities, 'facility_name', 'visit_count');

export const transformProviderScoreRadar = (provider) => {
  const score = getSafeObject(provider?.productivity_score);
  return [
    safeNumber(score.seen_rate_score),
    safeNumber(score.volume_score),
    safeNumber(score.revenue_score),
    safeNumber(score.low_no_show_score),
  ];
};

export const transformProviderTrend = (trend) => {
  const points = Array.isArray(trend?.points) ? trend.points : [];
  return {
    buckets: points.map((p) => p.bucket),
    patients: points.map((p) => safeNumber(p.patient_count)),
    visits: points.map((p) => safeNumber(p.visit_count)),
    revenue: points.map((p) => safeNumber(p.revenue)),
  };
};

export const transformProviderBenchmark = (benchmark) => {
  const metrics = getSafeObject(benchmark?.benchmark);
  const labels = {
    visit_count: 'Visits',
    patient_count: 'Patients',
    revenue: 'Revenue',
    seen_rate_pct: 'Seen Rate %',
    no_show_rate_pct: 'No-Show Rate %',
    productivity_score: 'Productivity Score',
  };
  return Object.entries(labels)
    .filter(([key]) => metrics[key])
    .map(([key, name]) => ({
      name,
      providerValue: safeNumber(metrics[key]?.provider_value),
      orgMean: safeNumber(metrics[key]?.org_mean),
      percentile: metrics[key]?.org_percentile,
    }));
};

// ---------- Fee Schedule Analytics ----------

export const transformFeeNameValues = (items, limit) => toNameValueList(items, 'name', 'value', limit);
